/**
 * 카드 외곽선 8접점(rounded_quad_8_tangent_points) 기하 유틸.
 *
 * 출처: card-match-testapp/src/main.ts 포팅 (라벨 스키마 v3 와 동일 모델).
 * outline = [c0진입, c0진출, c1진입, c1진출, c2진입, c2진출, c3진입, c3진출]
 *   — 카드 모서리가 라운드라 코너당 접점 2개(진입/진출)로 표현한다.
 * 가상 코너(virtualCorners)는 인접 변의 연장 교점 — 매칭 warp 는 이 4점을 쓴다.
 */

export interface Pt {
  x: number;
  y: number;
}

/** 라운드 접점이 코너에서 변 길이의 몇 %만큼 안쪽인가 (라벨러 DEFAULT_INSET 과 동일) */
export const ROUND_INSET = 0.04;

/** 두 직선(p1-p2, p3-p4)의 교점. 평행(퇴화)하면 p2,p3 중점 폴백 */
export function lineIntersection(p1: Pt, p2: Pt, p3: Pt, p4: Pt): Pt {
  const d = (p1.x - p2.x) * (p3.y - p4.y) - (p1.y - p2.y) * (p3.x - p4.x);
  if (Math.abs(d) < 1e-9) {
    return { x: (p2.x + p3.x) / 2, y: (p2.y + p3.y) / 2 };
  }
  const a = p1.x * p2.y - p1.y * p2.x;
  const b = p3.x * p4.y - p3.y * p4.x;
  return {
    x: (a * (p3.x - p4.x) - (p1.x - p2.x) * b) / d,
    y: (a * (p3.y - p4.y) - (p1.y - p2.y) * b) / d,
  };
}

/** 8접점 → 가상 코너 4개 (인접 변 연장 교점) */
export function virtualCorners(pts: Pt[]): Pt[] {
  if (pts.length !== 8) return [];
  const corners: Pt[] = [];
  for (let i = 0; i < 4; i++) {
    const prevExit = pts[((i + 3) % 4) * 2 + 1];
    const entry = pts[i * 2];
    const exit = pts[i * 2 + 1];
    const nextEntry = pts[((i + 1) % 4) * 2];
    corners.push(lineIntersection(prevExit, entry, exit, nextEntry));
  }
  return corners;
}

/** 코너 4개 → 8접점 (각 코너에서 이전/다음 코너 방향으로 ROUND_INSET 만큼 안쪽) */
export function outlineFromCorners(corners: Pt[]): Pt[] {
  const pts: Pt[] = [];
  for (let i = 0; i < 4; i++) {
    const c = corners[i];
    const prev = corners[(i + 3) % 4];
    const next = corners[(i + 1) % 4];
    pts.push({
      x: c.x + (prev.x - c.x) * ROUND_INSET,
      y: c.y + (prev.y - c.y) * ROUND_INSET,
    });
    pts.push({
      x: c.x + (next.x - c.x) * ROUND_INSET,
      y: c.y + (next.y - c.y) * ROUND_INSET,
    });
  }
  return pts;
}

/** 검출 실패 시 시드 — 중앙에 카드 비율(63:88) 사각형 (맞춤 폭의 70%) */
export function defaultQuad(w: number, h: number): Pt[] {
  const cardRatio = 88 / 63;
  let qw = w * 0.7;
  let qh = qw * cardRatio;
  if (qh > h * 0.8) {
    qh = h * 0.8;
    qw = qh / cardRatio;
  }
  const cx = w / 2;
  const cy = h / 2;
  return [
    { x: cx - qw / 2, y: cy - qh / 2 },
    { x: cx + qw / 2, y: cy - qh / 2 },
    { x: cx + qw / 2, y: cy + qh / 2 },
    { x: cx - qw / 2, y: cy + qh / 2 },
  ];
}

/**
 * 라운드 사각 Path — 변은 직선, 코너는 가상 코너를 제어점으로 하는 2차 베지어.
 * canvasPts/controls 는 이미 캔버스 좌표로 매핑된 값이어야 한다.
 */
export function buildOutlinePath(canvasPts: Pt[], controls: Pt[]): Path2D {
  const path = new Path2D();
  path.moveTo(canvasPts[1].x, canvasPts[1].y); // corner0 진출
  for (let i = 1; i < 4; i++) {
    path.lineTo(canvasPts[i * 2].x, canvasPts[i * 2].y);
    path.quadraticCurveTo(controls[i].x, controls[i].y, canvasPts[i * 2 + 1].x, canvasPts[i * 2 + 1].y);
  }
  path.lineTo(canvasPts[0].x, canvasPts[0].y);
  path.quadraticCurveTo(controls[0].x, controls[0].y, canvasPts[1].x, canvasPts[1].y);
  path.closePath();
  return path;
}
